import * as React from 'react'

import { SortDirection } from '@factorialco/deprecated-design-system'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import type Company from 'modules/core/store/models/Company'

import useIncidenceTypeQueryParam from 'modules/bookkeepers/hooks/useIncidenceTypeQueryParam'
import { FACTORIAL_APP, PROFESSIONALS_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import {
  getIncidenceTypeNavigationMapping,
  incidenceTypeNavigation,
} from 'modules/bookkeepers/lib/incidenceTypeNavigation'
import { SORT_ATTRIBUTES } from 'modules/bookkeepers/lib/tableSortColumns'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'

export interface FilterDate {
  value?: string
  firstDate?: string
  lastDate?: string
}

export interface IFilters {
  name?: string
  status?: string
  legal_entity_id?: string
  company_id?: string
  custom_leave_name?: string
}

export interface DefaultParams {
  filters?: IFilters
  filterDate?: FilterDate
  direction?: SortDirection
  attribute?: string
  selectedIncidenceType?: string
  containsMessage?: boolean
}

export interface UseIncidenceFilterReturn {
  filters: IFilters
  filterDate: FilterDate
  page: number
  hasMore: boolean
  search: string
  debouncedSearch: string
  direction: SortDirection
  attribute: string
  selectedCompany: Company | null
  selectedIncidenceType: string
  parsedFilterName: string[] | undefined
  containsMessage: boolean
  onSort: (attribute: string, direction: SortDirection) => void
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  setHasMore: (hasMore: boolean) => void
  onFilterChange: (field: keyof IFilters, value: string | string[]) => void
  onDateChange: (value: string) => void
  onPageChange: (page: number) => void
  setSelectedCompany: (company: Company | null) => void
  setSelectedIncidenceType: (incidenceType: string) => void
  setFilterDate: (filterDate: FilterDate) => void
}

export interface UseIncidenceFiltersType {
  filters: IFilters
  filterDate: FilterDate
}

const useIncidenceFilters = ({
  filters: defaultFilters,
  filterDate: defaultFilterDate,
  direction: defaultDirection,
  attribute: defaultAttribute,
  selectedIncidenceType: defaultSelectedIncidenceType,
  containsMessage: defaultContainsMessage,
}: DefaultParams): UseIncidenceFilterReturn => {
  const { incidenceType } = useIncidenceTypeQueryParam()
  const [filters, setFilters] = React.useState<IFilters>(defaultFilters ?? {})
  const [filterDate, setFilterDate] = React.useState<FilterDate>(defaultFilterDate ?? {})
  const [page, setPage] = React.useState<number>(1)
  const [hasMore, setHasMore] = React.useState<boolean>(false)
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const hasMessages =
    defaultContainsMessage ?? incidenceType === incidenceTypeNavigation.ALL_WITH_MESSAGES
  const [direction, setDirection] = React.useState<SortDirection>(
    hasMessages ? 'desc' : (defaultDirection ?? 'asc')
  )
  const [attribute, setAttribute] = React.useState<string>(defaultAttribute ?? '')
  const [containsMessage, setContainsMessage] = React.useState<boolean>(hasMessages)
  const [selectedCompany, setSelectedCompany] = React.useState<Company | null>(null)
  const [selectedIncidenceType, setSelectedIncidenceType] = React.useState<string>(
    defaultSelectedIncidenceType ?? incidenceTypeNavigation.ALL
  )
  useEffect(() => {
    if (hasMessages) {
      setDirection('desc')
      setAttribute(SORT_ATTRIBUTES.incidence_effective_date)
    } else {
      setDirection(defaultDirection ?? 'asc')
      setAttribute(defaultAttribute ?? '')
    }
    setContainsMessage(hasMessages)
  }, [defaultDirection, defaultAttribute, hasMessages])

  useEffect(() => {
    onPageChange(1)
  }, [incidenceType])

  const isProfessionals = useBookkeeperView()
  const { hasCompensations: hasCompensationsModule } = useHasCompensations()
  const mapping = getIncidenceTypeNavigationMapping()

  const parsedFilterName = isProfessionals
    ? incidenceType
      ? mapping[incidenceType]
      : undefined
    : filters.name?.split(',')

  const onSort = (attribute: string, direction: SortDirection) => {
    setDirection(direction)
    setAttribute(attribute)
    onPageChange(1)
  }

  const onFilterChange = (filterName: keyof IFilters, value: string | string[]) => {
    if (hasCompensationsModule) {
      const filterNameMap: Record<string, string> = {
        legal_entity_id: 'legal_entities',
      }

      const displayName = filterNameMap[filterName] || filterName

      track(trackingEvents.compensations.applyEmployeeUpdateFilter, {
        application: isProfessionals ? PROFESSIONALS_APP : FACTORIAL_APP,
        filter_name: displayName,
      })
    }

    setFilters((prevFilters): IFilters => {
      const newFilters = { ...prevFilters }
      const valueArray = value === undefined ? [''] : Array.isArray(value) ? value : [value]

      if (valueArray.length === 0 || valueArray[0] === '') {
        delete newFilters[filterName]
      } else {
        newFilters[filterName] = valueArray.join(',')
      }

      return newFilters
    })

    onPageChange(1)
  }

  const onDateChange = (value: string) => {
    if (filterDate.value === value || !value) setFilterDate({})
    else {
      const dateValue = stringToDate(value, 'ui')
      const firstDate = dateValue.startOf('month').format('YYYY-MM-DD')
      const lastDate = dateValue.endOf('month').format('YYYY-MM-DD')

      setFilterDate({ value, firstDate, lastDate })
    }

    onPageChange(1)
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(event)
    onPageChange(1)
  }

  const onPageChange = (page: number) => {
    setPage(page)
    setHasMore(false)
  }

  return {
    filters,
    filterDate,
    page,
    hasMore,
    search,
    debouncedSearch,
    direction,
    attribute,
    selectedCompany,
    selectedIncidenceType,
    parsedFilterName,
    containsMessage,
    onSearch: handleSearch,
    onSort,
    setHasMore,
    onFilterChange,
    onDateChange,
    onPageChange,
    setSelectedCompany,
    setSelectedIncidenceType,
    setFilterDate,
  }
}

export default useIncidenceFilters
